/* *******************************************************
 * *******************************************************
 * !!! Versuch mit top level properties
 * *******************************************************
 * *******************************************************
 */
import { TeoEventTarget } from '../utility/TeoEventTarget';

type ExtractNonFunctionKeys<T extends object> = {
  [K in keyof T]: K extends 'events' | '_events' | '*' ? never
  : // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  T[K] extends Function ? never
  : K & string;
}[keyof T];

type ExtractStoreEvents<T extends object> = {
  [K in ExtractNonFunctionKeys<T>]: (value: T[K]) => void;
} & { '*': (store: T) => void };

type ExtractNonFunctionProps<T extends object> = {
  [K in ExtractNonFunctionKeys<T>]: T[K];
};

export type FlatStoreExtensions<T extends object> = {
  addEventListener: <TKey extends keyof ExtractStoreEvents<T>>(
    key: TKey,
    listener: ExtractStoreEvents<T>[TKey],
  ) => () => void;
  setStateValue: <TKey extends ExtractNonFunctionKeys<T>>(
    key: TKey,
    value: ExtractNonFunctionProps<T>[TKey],
    force?: boolean,
  ) => void;
  subscribe: (cb: (value: T) => void) => () => void;
};

export interface StoreClassType<T extends object> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  new (...args: any[]): FlatStoreExtensions<T>;
}

export function FlatStore<T extends object>(): StoreClassType<T> {
  const clazz = class FlatStoreTClass implements FlatStoreExtensions<T> {
    protected _events = new TeoEventTarget<ExtractStoreEvents<T>>();

    addEventListener<TKey extends keyof ExtractStoreEvents<T>>(
      key: TKey,
      listener: ExtractStoreEvents<T>[TKey],
    ) {
      return this._events.addEventListener(key, listener);
    }

    setStateValue<TKey extends ExtractNonFunctionKeys<T>>(
      key: TKey,
      value: ExtractNonFunctionProps<T>[TKey],
      force = false,
    ) {
      const self = this as unknown as ExtractNonFunctionProps<T>;
      if (!force && Object.is(self[key], value)) return;
      self[key] = value;
      this._events.triggerEvent(key as keyof ExtractStoreEvents<T>, value as never);
      this._events.triggerEvent('*', this as unknown as T);
    }

    subscribe(cb: (value: T) => void) {
      console.log('Subscribe', this);
      cb(this as unknown as T);
      return this._events.addEventListener('*', (value) => {
        cb(value as T);
      });
    }
  };
  return clazz as unknown as StoreClassType<T>;
}

/**
 * Like {@link FlatStore}, but every instance is wrapped in a `Proxy` so a plain
 * assignment — `store.name = 'Chef'` — automatically:
 *   1. writes the value onto the instance, then
 *   2. fires `triggerEvent('<key>', value)` and `triggerEvent('*', store)`.
 *
 * No explicit `setStoreValue` call is needed; it stays available and simply
 * routes the write back through the same proxy trap (so events fire once).
 */
export function FlatStoreProxy<T extends object>(): StoreClassType<T> {
  type Internals = { _events: TeoEventTarget<ExtractStoreEvents<T>> };

  const handler: ProxyHandler<object> = {
    set(target, prop, value) {
      const previous = Reflect.get(target, prop);
      const didSet = Reflect.set(target, prop, value);
      // Only emit for real, changed data props — never for `_events`, symbols,
      // or no-op writes (mirrors the early-return guard in `setStoreValue`).
      if (didSet && typeof prop === 'string' && prop !== '_events' && !Object.is(previous, value)) {
        const { _events } = target as unknown as Internals;
        _events.triggerEvent(prop as keyof ExtractStoreEvents<T>, value as never);
        _events.triggerEvent('*', target as unknown as T);
      }
      return didSet;
    },
  };

  const clazz = class FlatStoreProxyClass {
    protected _events = new TeoEventTarget<ExtractStoreEvents<T>>();

    constructor() {
      // Returning an object from a constructor replaces `this` with it — for a
      // direct `new` and for subclasses, whose `super()` then yields this proxy.
      return new Proxy(this, handler) as FlatStoreProxyClass;
    }

    addEventListener<TKey extends keyof ExtractStoreEvents<T>>(
      key: TKey,
      listener: ExtractStoreEvents<T>[TKey],
    ) {
      return this._events.addEventListener(key, listener);
    }

    setStateValue<TKey extends ExtractNonFunctionKeys<T>>(
      key: TKey,
      value: ExtractNonFunctionProps<T>[TKey],
    ) {
      // `this` is the proxy, so the assignment hits the `set` trap above, which
      // performs the write and fires both events exactly once.
      (this as unknown as ExtractNonFunctionProps<T>)[key] = value;
    }
  };
  return clazz as unknown as StoreClassType<T>;
}

/** Read/write accessor for a single value at a recorded path. */
type LeafAccessor<V> = {
  get(): V;
  /** Set a new value, or pass an updater `(current) => next` (à la React/Svelte). */
  set(value: V | ((current: V) => V)): void;
};

/**
 * Recursive accessor type: every node can `get()`/`set()` its own value and be
 * navigated further. Objects expose their keys, arrays expose numeric indexing —
 * each yielding another `DeepAccessor`. Primitives are leaves.
 */
type DeepAccessor<V> =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  [V] extends [Function] ? LeafAccessor<V>
  : [V] extends [readonly (infer E)[]] ? LeafAccessor<V> & { [index: number]: DeepAccessor<E> }
  : [V] extends [object] ? LeafAccessor<V> & { [K in keyof V]-?: DeepAccessor<V[K]> }
  : LeafAccessor<V>;

/** Public instance shape: top-level data keys become accessors, plus the store API. */
export type DeepStoreInstance<T extends object> = {
  [K in ExtractNonFunctionKeys<T>]: DeepAccessor<ExtractNonFunctionProps<T>[K]>;
} & Omit<FlatStoreExtensions<T>, 'subscribe'> & {
    // `subscribe` yields the accessor view — the proxy passes itself at runtime —
    // so `$store.foo.bar.set(...)` type-checks instead of resolving to raw `T`.
    subscribe: (cb: (value: DeepStoreInstance<T>) => void) => () => void;
    /** The raw underlying data, for direct reads such as `store.raw.address.street`. */
    readonly raw: ExtractNonFunctionProps<T>;
  };

export interface DeepStoreClassType<T extends object> {
  new (initial: ExtractNonFunctionProps<T>): DeepStoreInstance<T>;
}

/**
 * Like {@link FlatStoreProxy}, but instead of proxying every value it *records the
 * path* you navigate. A property access never returns the raw value — it returns a
 * lightweight path accessor, so nothing is read or wrapped until you ask for it:
 *
 *   store.address.street            // accessor recording ['address', 'street']
 *   store.address.street.get()      // → the actual value
 *   store.address.street.set('X')   // writes it; if changed, fires the TOP key ('address') + '*'
 *   store.names[3].get()            // arrays work too (top key here is 'names')
 *
 * For a plain, direct read without `.get()`, use `store.raw`:
 *   store.raw.address.street        // → the value (reads live data; writing through it skips events)
 *
 * Writes mutate in place — the top-level object keeps its reference and the fired
 * event is the change signal. The data shape is the type parameter and initial
 * values are passed to the constructor (a subclass's own field declarations can't
 * be remapped to accessors, so the self-referencing form isn't used here).
 */
export function FlatStoreDeepProxy<T extends object>(): DeepStoreClassType<T> {
  type Data = ExtractNonFunctionProps<T>;
  type Internals = { _events: TeoEventTarget<ExtractStoreEvents<T>>; _data: Data };
  /* eslint-disable @typescript-eslint/no-explicit-any */

  const readPath = (data: any, path: PropertyKey[]): unknown =>
    path.reduce((acc, key) => (acc == null ? undefined : acc[key]), data);

  const writePath = (
    internals: Internals,
    root: object,
    path: PropertyKey[],
    valueOrUpdater: unknown,
  ) => {
    const data = internals._data as any;
    const parent = path.slice(0, -1).reduce((acc, key) => acc[key], data);
    const leaf = path[path.length - 1] as any;
    const current = parent[leaf];
    // Accept either a new value or an updater `(current) => next`.
    const value =
      typeof valueOrUpdater === 'function' ?
        (valueOrUpdater as (c: unknown) => unknown)(current)
      : valueOrUpdater;
    if (Object.is(current, value)) return;
    parent[leaf] = value; // mutate in place — the top-level reference is unchanged
    const topKey = path[0] as keyof ExtractStoreEvents<T>;
    internals._events.triggerEvent(topKey, data[topKey] as never);
    internals._events.triggerEvent('*', root as unknown as T);
  };

  const makeAccessor = (internals: Internals, root: object, path: PropertyKey[]): unknown =>
    new Proxy(() => {}, {
      get(_t, prop) {
        if (prop === 'get') return () => readPath(internals._data, path);
        if (prop === 'set') return (value: unknown) => writePath(internals, root, path, value);
        // `then` guard keeps accessors from being mistaken for thenables (e.g. when awaited).
        if (prop === 'then' || typeof prop === 'symbol') return undefined;
        return makeAccessor(internals, root, [...path, prop]);
      },
    });

  const rootHandler: ProxyHandler<object> = {
    get(target, prop, receiver) {
      const internals = target as unknown as Internals;
      // A key present in the data starts a path accessor; everything else
      // (methods, `raw`, `_data`/`_events`, symbols, `toString`…) is the real member.
      if (typeof prop !== 'symbol' && prop !== '_data' && prop in internals._data) {
        return makeAccessor(internals, receiver, [prop]);
      }
      return Reflect.get(target, prop, receiver);
    },
  };

  const clazz = class FlatStoreDeepProxyClass {
    protected _events = new TeoEventTarget<ExtractStoreEvents<T>>();
    protected _data: Data;

    constructor(initial: Data) {
      this._data = initial;
      // The returned proxy becomes `this` (for `new` and for subclasses' `super()`).
      return new Proxy(this, rootHandler) as FlatStoreDeepProxyClass;
    }

    addEventListener<TKey extends keyof ExtractStoreEvents<T>>(
      key: TKey,
      listener: ExtractStoreEvents<T>[TKey],
    ) {
      return this._events.addEventListener(key, listener);
    }

    /** Top-level convenience write — same effect as `store.<key>.set(value)`. */
    setStateValue<TKey extends ExtractNonFunctionKeys<T>>(
      key: TKey,
      value: ExtractNonFunctionProps<T>[TKey],
    ) {
      writePath(this as unknown as Internals, this, [key], value);
    }

    subscribe(cb: (value: typeof this) => void) {
      cb(this);
      return this._events.addEventListener('*', (value) => cb(value as typeof this));
    }

    get raw(): Data {
      return this._data;
    }
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */
  return clazz as unknown as DeepStoreClassType<T>;
}
